"use client";

import { useEffect, useState, useRef } from "react";
import { Trophy, CheckCircle } from "lucide-react";
import axios from "axios";
import { eventEmitter } from "./twitchWebsocket";
import {
  NyeremenyjatekProp,
  NyeremenyjatekDisplay,
  Winner,
  WinnerSlotProps,
} from "./nyeremenyjatekDisplay";
import sendMessage from "../hooks/sendMessage";

export default function Nyeremenyjatek({
  nyeremenyjatekData,
  removeWinner,
}: Readonly<{
  nyeremenyjatekData: NyeremenyjatekProp;
  removeWinner?: number;
}>) {
  const [winners, setWinners] = useState<Winner[]>([]);
  const [allParticipants, setAllParticipants] = useState<string[]>([]);
  const [currentRolling, setCurrentRolling] = useState<number | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [frozenParticipants, setFrozenParticipants] = useState<string[]>([]);
  const [removingWinners, setRemovingWinners] = useState<Set<number>>(
    new Set(),
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const queueRef = useRef<number[]>([]);
  const processedSetRef = useRef<Set<number>>(new Set());
  const queuedSetRef = useRef<Set<number>>(new Set());
  const runningRef = useRef(false);

  useEffect(() => {
    if (!nyeremenyjatekData.tickets) return;
    const tickets = Array.isArray(nyeremenyjatekData.tickets)
      ? nyeremenyjatekData.tickets.map((t) => t.twitch_name)
      : [];
    if (!isAnimating) {
      setAllParticipants(tickets);
      setFrozenParticipants(tickets);
    } else {
      setAllParticipants(tickets);
    }
  }, [nyeremenyjatekData.tickets, isAnimating]);

  // Merge in new winners by appending them to the end of the list
  useEffect(() => {
    const raw = (nyeremenyjatekData.winners ?? []).slice();
    if (raw.length === 0) {
      // Reset everything
      setWinners([]);
      setIsAnimating(false);
      setCurrentRolling(null);
      queueRef.current = [];
      processedSetRef.current.clear();
      queuedSetRef.current.clear();
      runningRef.current = false;
      return;
    }

    // Sort by selection time
    raw.sort((a: any, b: any) => {
      const at = a.selectedAt ? new Date(a.selectedAt).getTime() : 0;
      const bt = b.selectedAt ? new Date(b.selectedAt).getTime() : 0;
      return at - bt;
    });

    // Build new winners list: keep existing ones in order, then append any new ones
    setWinners((prev) => {
      const prevById = new Map(prev.map((w) => [w.twitch_id, w]));
      const existing: Winner[] = [];
      const newEntries: any[] = [];

      raw.forEach((w: any) => {
        if (prevById.has(w.twitch_id)) {
          // preserve previous revealed/verified state
          existing.push(prevById.get(w.twitch_id)!);
        } else {
          newEntries.push(w);
        }
      });

      const appended: Winner[] = newEntries.map((w) => ({
        name: w.twitch_name,
        twitch_id: w.twitch_id,
        position: 0, // position will be recalculated below
        revealed: false,
        verified: false,
      }));

      // Combine: existing revealed + new unrevealed
      const combined = [...existing, ...appended];

      // Recalculate positions
      const final = combined.map((w, idx) => ({
        ...w,
        position: idx + 1,
      }));

      // Enqueue only the *new* winners
      appended.forEach((w) => {
        if (!queuedSetRef.current.has(w.twitch_id)) {
          queueRef.current.push(w.twitch_id);
          queuedSetRef.current.add(w.twitch_id);
        }
      });

      // Start processing if needed
      if (queueRef.current.length > 0 && !runningRef.current) {
        runningRef.current = true;
        setIsAnimating(true);
        setTimeout(() => processQueue(), 0);
      }

      return final;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nyeremenyjatekData.winners]);

  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

  const processQueue = async () => {
    while (queueRef.current.length > 0) {
      const twitchId = queueRef.current.shift()!;

      // Wait for DOM to update
      await sleep(50);

      // Find the index of that winner in the DOM
      const elems = Array.from(document.querySelectorAll(".winner-slot"));
      const idx = elems.findIndex(
        (el) => el.getAttribute("data-twitch-id") === String(twitchId),
      );

      if (idx < 0) {
        console.warn("Winner not found in DOM:", twitchId);
        continue;
      }

      setCurrentRolling(idx);

      // Scroll to the rolling winner
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        const targetElement = elems[idx] as HTMLElement;
        const containerHeight = container.clientHeight;
        const elementTop = targetElement.offsetTop;
        const elementHeight = targetElement.offsetHeight;
        const target = elementTop - containerHeight / 2 + elementHeight / 2;

        container.scrollTo({
          top: Math.max(0, target),
          behavior: "smooth",
        });
      }

      await sleep(2600);

      setWinners((prev) =>
        prev.map((w) =>
          w.twitch_id === twitchId ? { ...w, revealed: true } : w,
        ),
      );

      processedSetRef.current.add(twitchId);
      setCurrentRolling(null);
      await sleep(100); // Wait for state update

      // Get the winner data from the current state after the update
      const winnerData = await new Promise<Winner | undefined>((resolve) => {
        setWinners((prev) => {
          const winner = prev.find((w) => w.twitch_id === twitchId);
          resolve(winner);
          return prev;
        });
      });

      console.log(winnerData);

      if (winnerData) {
        setTimeout(() => {
          sendMessage(
            `🎁 @${winnerData.name} won the giveaway, type in chat!`,
          ).catch((e) => console.error("Failed to send message:", e));
        }, 5000);
      }

      if (queueRef.current.length > 0) {
        await sleep(1000);
      }
    }

    runningRef.current = false;
    setIsAnimating(false);
  };

  useEffect(() => {
    const handleTwitchMessage = (messageData: {
      username: string;
      twitch_id: number;
      message: string;
      timestamp: Date;
    }) => {
      setWinners((prev) =>
        prev.map((winner) =>
          winner.twitch_id === messageData.twitch_id &&
          winner.revealed &&
          !winner.verified
            ? { ...winner, verified: true }
            : winner,
        ),
      );
    };
    eventEmitter.on("twitchMessage", handleTwitchMessage);
    return () => {
      eventEmitter.off("twitchMessage", handleTwitchMessage);
    };
  }, []);

  const handleRemoveWinner = async (twitch_id: number) => {
    setRemovingWinners((prev) => new Set(prev).add(twitch_id));
    await sleep(500);

    // Remove from display
    setWinners((prev) => {
      const filtered = prev.filter((w) => w.twitch_id !== twitch_id);
      // Recalculate positions
      return filtered.map((w, idx) => ({ ...w, position: idx + 1 }));
    });

    // Allow that ID to be drawn again
    processedSetRef.current.delete(twitch_id);
    queuedSetRef.current.delete(twitch_id);

    setRemovingWinners((prev) => {
      const nxt = new Set(prev);
      nxt.delete(twitch_id);
      return nxt;
    });

    // If new winners have already been queued, ensure they start rolling
    if (queueRef.current.length > 0 && !runningRef.current) {
      runningRef.current = true;
      setIsAnimating(true);
      setTimeout(() => processQueue(), 0);
    }
  };

  useEffect(() => {
    if (removeWinner !== undefined) {
      handleRemoveWinner(removeWinner);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeWinner]);

  if ((nyeremenyjatekData.winners?.length ?? 0) > 0) {
    return (
      <div className="absolute flex items-center justify-center nyeremenyjatek-container rounded-3xl">
        <div className="relative w-full h-full bg-gradient-to-b from-slate-800/90 to-slate-900/90 backdrop-blur-xl rounded-3xl p-2 shadow-2xl border border-slate-700/50 animate-[scale_700ms_ease-in-out]">
          <div
            ref={scrollContainerRef}
            className="space-y-1 max-h-[480px] overflow-y-auto pr-2 custom-scrollbar"
          >
            {winners.map((winner, i) => (
              <WinnerSlot
                key={`${winner.twitch_id}-${winner.position}`}
                winner={winner}
                isRolling={currentRolling === i}
                allParticipants={frozenParticipants}
                isRemoving={removingWinners.has(winner.twitch_id)}
                onRemove={
                  removeWinner !== undefined
                    ? () => handleRemoveWinner(winner.twitch_id)
                    : undefined
                }
                className="winner-slot"
                data-twitch-id={winner.twitch_id}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <NyeremenyjatekDisplay
      data={{
        ...nyeremenyjatekData,
        tickets: allParticipants.map(
          (name) =>
            ({
              twitch_name: name,
            }) as any,
        ),
      }}
    />
  );
}

function WinnerSlot({
  winner,
  isRolling,
  allParticipants,
  isRemoving,
  onRemove,
  className,
  ...props
}: Readonly<
  WinnerSlotProps & {
    className?: string;
    isRemoving?: boolean;
    onRemove?: () => void;
    [key: string]: any;
  }
>) {
  const [rollingNames] = useState(() => {
    if (allParticipants.length === 0) return [];
    const shuffled = [...allParticipants].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, Math.min(8, shuffled.length));
  });

  return (
    <div
      {...props}
      className={`relative w-full transition-all duration-500 ${
        isRemoving ? "opacity-0 -translate-x-full" : "opacity-100 translate-x-0"
      } ${className || ""}`}
    >
      <div
        className={`
          relative bg-gradient-to-r from-slate-800/80 to-slate-800/60 rounded-lg p-2 border-2 transition-all duration-300 group
          ${
            winner.revealed
              ? winner.verified
                ? "border-green-500/50 shadow-lg shadow-green-500/20"
                : "border-[#8e00ff99]/50 shadow-lg shadow-purple-500/20"
              : isRolling
                ? "border-[#8e00ff50] shadow-md shadow-purple-500/10"
                : "border-[#8e00ff30]"
          }
        `}
      >
        <div className="flex items-center gap-2">
          <div className="flex-shrink-0">
            <div
              className={`
                w-6 h-6 rounded-md flex items-center justify-center font-bold text-sm transition-all duration-300
                ${
                  winner.revealed
                    ? winner.verified
                      ? "bg-green-600 text-white"
                      : "bg-gradient-to-br from-purple-700 to-purple-600 text-white"
                    : isRolling
                      ? "bg-purple-700/70 text-purple-200"
                      : "bg-purple-700/30 text-slate-500"
                }
              `}
            >
              {winner.position}
            </div>
          </div>

          <div className="flex-1 overflow-hidden h-5 relative">
            {isRolling ? (
              <div className="slot-roll">
                {rollingNames.map((name, idx) => (
                  <div
                    key={`${name}-${idx}`}
                    className="text-base font-semibold text-slate-300 h-5 flex items-center"
                  >
                    {name}
                  </div>
                ))}
              </div>
            ) : (
              <div
                className={`
                  text-base font-semibold h-5 flex items-center transition-all duration-500
                  ${
                    winner.revealed
                      ? "text-white winner-reveal"
                      : "text-transparent"
                  }
                `}
              >
                {winner.name}
              </div>
            )}
          </div>

          {winner.revealed && (
            <div className="flex-shrink-0 winner-reveal flex items-center gap-2">
              {winner.verified && (
                <CheckCircle
                  className="w-4 h-4 text-green-400"
                  strokeWidth={2.5}
                />
              )}
              <Trophy className="w-4 h-4 text-yellow-400" strokeWidth={2.5} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export async function getActiveNyeremenyjatek() {
  const response = await axios.get(
    "https://api.palinagyonbuzi.com/api/nyeremenyjatek/active",
    {
      headers: {
        "Cache-Control": "no-store",
        Authorization: "Bearer f2414060-927a-411b-a481-197241e5828d",
      },
    },
  );
  return response.data;
}
